import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Link,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import React from "react";
import { useAlert } from "react-alert";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";

import { updateProjectPhiAllowlist } from "../actions/environments";
import FreshpaintUser from "../components/users/FreshpaintUser";
import Unauthorized from "../components/users/Unauthorized";
import useCurrentProject from "../hooks/use-project";
import useAccountMetadata from "../hooks/useAccountMetadata";
import useUsers from "../hooks/useUsers";
import Navigation from "../Navigation";
import { PropertySensitivityLevels } from "../types/global";
import { PHIAllowListContainer } from "./PHIAllowListContainer";
import { AllowListType } from "./types";

const useStyles = makeStyles(() => ({
  body: {
    margin: 24,
  },
  card: {
    width: "50%",
    minWidth: 800,
  },
  cardContent: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
}));

const PHIAllowListPage: React.FC = () => {
  const alert = useAlert();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const accountMetadata = useAccountMetadata();
  const currentProject = useCurrentProject();
  const usersState = useUsers();

  const handleSave = async (
    allowListType: AllowListType,
    newSensitivityLevels: PropertySensitivityLevels
  ): Promise<void> => {
    return (dispatch(
      updateProjectPhiAllowlist(alert, allowListType, newSensitivityLevels)
    ) as any).catch((error: any) => {
      alert.error("Something went wrong. Unable to save allowlist.");
      console.log(error);
      Sentry.captureException(error);
    });
  };

  if (!accountMetadata.loaded || !currentProject.loaded) {
    return null;
  }

  return (
    <Navigation className="pt-6 h-full flex flex-col">
      {usersState.loading ? (
        <>Loading</>
      ) : !accountMetadata.config.rbac ||
        new FreshpaintUser(usersState.currentUser).hasAdminAccess() ? (
        <>
          <Typography component="h2" gutterBottom variant="overline">
            HIPAA Allow List
          </Typography>
          <Typography component="h1" gutterBottom variant="h3">
            Define the properties that do not contain health information.
          </Typography>
          <Card className={classes.card}>
            <CardHeader
              title={`${currentProject.name} Project PHI Allow List`}
            />
            <Divider />
            <CardContent className={clsx(classes.cardContent)}>
              <div className={clsx(classes.body)}>
                <Typography variant="body1">
                  Classify properties that are known to be safe to send to any
                  destination.
                  <br />
                  <br />
                  To classify properties on a per-destination basis, visit
                  the&nbsp;
                  <Link
                    color="initial"
                    className="text-brand-red"
                    onClick={() => history.push("/destinations")}
                  >
                    Destinations page
                  </Link>
                  , select the destination from the list, and click &quot;HIPAA
                  Allow List&quot;.
                  <br />
                  <br />
                  Refer to the&nbsp;
                  <Link
                    color="initial"
                    className="text-brand-red"
                    href="https://documentation.freshpaint.io/hipaa-mode#enforced-allowlists"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    HIPAA Mode enforced allowlists docs
                  </Link>
                  &nbsp;for more details.
                </Typography>
              </div>
              <Divider />
              <PHIAllowListContainer
                sensitivityLevels={{
                  event: currentProject.eventPropertySensitivityLevels,
                  user: currentProject.userPropertySensitivityLevels,
                  group: currentProject.groupPropertySensitivityLevels,
                }}
                onSave={handleSave}
              />
            </CardContent>
          </Card>
        </>
      ) : (
        <Unauthorized message="Only Admin can view / update HIPAA Allowlist" />
      )}
    </Navigation>
  );
};

export default PHIAllowListPage;
